import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import {useAuth} from '../../Auth/AuthContext'; 

// Hook personalizado para manejo de debounce
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const AniadirCarrera = ({ abrirModal, cerrarModal, onCarreraCreada }) => { 
    const {getToken} = useAuth(); 
    const [formData, setFormData] = useState({
        nombre: '', 
        areaSeleccionada: '', 
        universidadesSeleccionadas: []
    }); 
    const [areas, setAreas] = useState([]);
    const [universidades, setUniversidades] = useState([]); 
    const [cargandoAreas, setCargandoAreas] = useState(false);  
    const [errorAreas, setErrorAreas] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [validandoCarrera, setValidandoCarrera] = useState(false);
    const [errores, setErrores] = useState({ 
        nombre: '', 
        area: ''
    }); 
    const debouncedNombre = useDebounce(formData.nombre, 500);
    const token = getToken(); 

    // Estados de carga consolidados
    const loadingStates = useMemo(() => ({
        data: cargandoAreas,
        submit: enviando,
        validation: validandoCarrera
    }), [cargandoAreas, enviando, validandoCarrera]);

    // Datos consolidados
    const data = useMemo(() => ({
        areas,
        universidades
    }), [areas, universidades]);

    const validators = useMemo(() => ({ 
        nombre: (nombre) => { 
            return nombre.trim() ? '' : 'El nombre de la carrera no puede estar vacío.';
        }, 
        area: (area) => { 
            return area ? '' : 'Debes seleccionar un área académica.';
        }
    }), []); 

    const updateError = useCallback((field, error) => { 
        setErrores(prev => ({ ...prev, [field]: error})); 
    }, []); 

    const cargarDatos = useCallback(async () => { 
        setCargandoAreas(true); 
        setErrorAreas(''); 

        try { 
            const [responseAreas, responseUniversidades] = await Promise.all([ 
                fetch(`${process.env.REACT_APP_API_URL}/universidades/areas`), 
                fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`)
            ]); 

            if (!responseAreas.ok || !responseUniversidades.ok) {
                throw new Error(`Error al cargar datos: ${responseAreas.status} ${responseUniversidades.status}`);
            }

            const [areasData, universidadesData] = await Promise.all([ 
                responseAreas.json(), 
                responseUniversidades.json()
            ]); 

            const universidadesMapeadas = universidadesData 
                .filter(univ => univ && (univ.id !== null && univ.id !== undefined)) 
                .map((univ, index) => ({
                    un_clave: univ.id, 
                    un_nombre: univ.name, 
                    uniqueId: univ.id || `temp-${index}`
                })); 

            setAreas(areasData); 
            setUniversidades(universidadesMapeadas);
            
        } catch (error) { 
            console.error('Error al cargar datos:', error); 
            setErrorAreas('Error al cargar los datos necesarios'); 
            Swal.fire({ 
                title: 'Error', 
                text: `No se pudieron cargar los datos necesarios. ${error.message}`,
                icon: 'error', 
                confirmButtonText: 'Entendido', 
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito'
                }
            }); 
        } finally { 
            setCargandoAreas(false); 
        }
    }, []); 

    useEffect(() => { 
        if (abrirModal) { 
            cargarDatos(); 
        }
    }, [abrirModal, cargarDatos]);

    //Función para actualizar datos del formulario. 
    const handleInputChange = useCallback((field, value) => { 
        setFormData(prev => ({ ...prev, [field]: value })); 
        if (field === 'nombre') { 
            updateError('nombre', validators.nombre(value)); 
        } else if (field === 'areaSeleccionada') { 
            updateError('area', validators.area(value)); 
        }
    }, [validators, updateError]);

    const handleUniversidadChange = useCallback((un_clave) => { 
        setFormData(prev => ({ 
            ...prev, 
            universidadesSeleccionadas: prev.universidadesSeleccionadas.includes(un_clave)
            ? prev.universidadesSeleccionadas.filter(id => id !== un_clave)
            : [...prev.universidadesSeleccionadas, un_clave]
        })); 
    }, []); 

    // Verificar si la carrera existe. 
    const verificarCarreraExistente = useCallback(async (nombre) => {
        if (!nombre.trim()) return false; 

        try { 
            setValidandoCarrera(true); 
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/verificar-carrera/${encodeURIComponent(nombre)}`, {
                method: 'GET', 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) { 
                const data = await response.json(); 
                return data.existe; 
            }
            return false; 
        } catch (error) { 
            console.error('Error al verificar carrera:', error);
            return false; 
        } finally { 
            setValidandoCarrera(false); 
        }
    }, []); 

    //Manejo de modal. 
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setIsClosing(true);
        }
    }, []); 

    const handleCloseModal = useCallback(() => { 
        setIsClosing(true); 
    }, []); 

    //Validación de formulario. 
    const validateForm = useCallback(() => { 
        const nuevosErrores = { 
            nombre: validators.nombre(formData.nombre), 
            area: validators.area(formData.areaSeleccionada)
        }; 

        const hayErrores = !!(nuevosErrores.nombre || nuevosErrores.area);

        setErrores(nuevosErrores);
        return !hayErrores; 
    }, [formData, validators]); 

    //Universidades memoizadas para evitar rerenderizados. 
    const universidadesMemoizadas = useMemo(() => { 
        return universidades.map((universidad) => (
            <div key={universidad.un_clave} className={styles.checkboxItem}>
                <input 
                    type="checkbox"
                    id={`univ-${universidad.uniqueId}`}
                    checked={formData.universidadesSeleccionadas.includes(universidad.un_clave)}
                    onChange={() => handleUniversidadChange(universidad.un_clave)}
                    disabled={cargandoAreas || enviando} 
                />
                <label htmlFor={`univ-${universidad.uniqueId}`}>
                    {universidad.un_nombre}
                </label>
            </div>
        )); 
    }, [universidades, formData.universidadesSeleccionadas, cargandoAreas, enviando, handleUniversidadChange]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        try {
            setEnviando(true);
            
            const carreraExiste = await verificarCarreraExistente(formData.nombre.trim());
            if (carreraExiste) {
                Swal.fire({
                    title: 'Carrera existente',
                    text: 'Esta carrera ya existe en el sistema. Por favor, ingresa un nombre diferente.',
                    icon: 'warning',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: {
                        popup: 'popupbonito'
                    }
                });
                return;
            }
            
            const carreraData = {
                nombre: formData.nombre.trim(),
                ar_clave: parseInt(formData.areaSeleccionada),
                universidades: formData.universidadesSeleccionadas.map(id => parseInt(id))
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/crear-carrera`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(carreraData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al crear carrera');
            }
            
            Swal.fire({
                title: 'Carrera creada correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });

                if (onCarreraCreada) {
                    await onCarreraCreada(); 
                }
                
                setIsClosing(true);
                
        } catch (error) {
            console.error('Error al crear carrera:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'Ha ocurrido un error al crear la carrera.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setEnviando(false);
        }
    }, [formData, validateForm, verificarCarreraExistente]);

    // Efecto para validar carrera existente con debounce
    useEffect(() => {
        if (debouncedNombre && debouncedNombre.length > 2) {
            verificarCarreraExistente(debouncedNombre);
        }
    }, [debouncedNombre, verificarCarreraExistente]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
                setFormData({
                    nombre: '',
                    areaSeleccionada: '',
                    universidadesSeleccionadas: []
                });
                setErrores({ nombre: '', area: '' });
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalAniadirCont} ${abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''}`}>
                <div className={styles.modalScrollableAniadir}>
                    <button 
                        onClick={handleCloseModal} 
                        type="button" 
                        className={styles.closeBtnAniadir} 
                        disabled={loadingStates.submit}
                    >
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Añadir Carrera</h2>
                    
                    {loadingStates.data && (
                        <div className={styles.loading}>
                            <p>Cargando datos...</p>
                        </div>
                    )}
                    
                    <form className={styles.formUsuario} onSubmit={handleSubmit}>
                        <label htmlFor="nombre">
                            Nombre de la carrera:
                            <input
                                type="text"
                                id="nombre"
                                value={formData.nombre}
                                placeholder="Ingresa el nombre de la carrera"
                                onChange={(e) => handleInputChange('nombre', e.target.value)}
                                required
                                disabled={loadingStates.data || loadingStates.submit}
                            />
                            {loadingStates.validation && (
                                <small className={styles.validandoTexto}>Verificando disponibilidad...</small>
                            )}
                            {errores.nombre && <p className={styles.mensajeErrorAniadir}>{errores.nombre}</p>}
                        </label>

                        <label htmlFor="area">
                            Área académica:
                            <select
                                id="area"
                                value={formData.areaSeleccionada}
                                onChange={(e) => handleInputChange('areaSeleccionada', e.target.value)}
                                required
                                disabled={loadingStates.data || loadingStates.submit}
                            >
                                <option value="">Selecciona un área</option>
                                {data.areas.map((area) => (
                                    <option key={area.ar_clave} value={area.ar_clave}>
                                        {area.ar_nombre}
                                    </option>
                                ))}
                            </select>
                            {errores.area && <p className={styles.mensajeErrorAniadir}>{errores.area}</p>}
                        </label>

                        <div className={styles.seccionUniversidades}>
                            <label>Universidades que ofrecen esta carrera (opcional):</label>
                            {data.universidades.length === 0 && !loadingStates.data && (
                                <p className={styles.noUniversidades}>No se encontraron universidades disponibles.</p>
                            )}
                            <div className={styles.checkboxContainer}>
                                {universidadesMemoizadas}
                            </div>
                            {formData.universidadesSeleccionadas.length > 0 && (
                                <p className={styles.universidadesSeleccionadas}>
                                    {formData.universidadesSeleccionadas.length} universidad(es) seleccionada(s)
                                </p>
                            )}
                        </div>

                        <button 
                            type="submit" 
                            className={styles.botonGuard}
                            disabled={loadingStates.data || loadingStates.submit}
                        >
                            {loadingStates.submit ? 'Guardando...' : 'Guardar Carrera'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AniadirCarrera;